/**
 * DriverFormModal — create / edit driver form.
 *
 * Extracted from FleetPanel.jsx (Phase 2 P2 decomposition · 2026-06).
 * Behaviour is BYTE-IDENTICAL: same testids, same edit-detection
 * (`isEdit = !!driver`), same patch-only-changed-fields semantics on
 * update, same auto-generated-password reveal on create, same
 * password visibility toggle introduced in the earlier UX pass.
 */
import React, { useEffect, useState } from "react";
import { X, Loader2, Eye, EyeOff, Mail, Copy, Check, AlertTriangle } from "lucide-react";
import { api, formatApiError } from "../../../lib/api";
import { toast } from "sonner";
import { DocumentReviewer } from "../DocumentReviewer";

export const DriverFormModal = ({ driver, onClose, onSaved }) => {
  const isEdit = !!driver;
  const [tab, setTab] = useState("details");
  const [form, setForm] = useState({
    name: driver?.name || "",
    email: driver?.email || "",
    phone: driver?.phone || "",
    vehicle_make: driver?.vehicle_make || "",
    vehicle_plate: driver?.vehicle_plate || "",
    password: "",
  });
  const [saving, setSaving] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [sendingReset, setSendingReset] = useState(false);
  const [resetResult, setResetResult] = useState(null); // {reset_url, delivered, email_status}
  const [copied, setCopied] = useState(false);

  const sendResetLink = async () => {
    if (!driver?.id) return;
    setSendingReset(true);
    setCopied(false);
    try {
      const { data } = await api.post(`/admin/users/${driver.id}/send-reset-link`);
      setResetResult(data);
      if (data?.delivered) toast.success(data.message || "Password reset link sent.");
      else toast.warning(data?.message || "Email not delivered — copy the link below.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSendingReset(false);
    }
  };

  const copyResetLink = async () => {
    if (!resetResult?.reset_url) return;
    try {
      await navigator.clipboard.writeText(resetResult.reset_url);
      setCopied(true);
      toast.success("Reset link copied to clipboard.");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Couldn't copy — select and copy the link manually.");
    }
  };

  // ESC dismisses (matches ConfirmModal behaviour)
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const onField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || (!isEdit && !form.email.trim())) {
      toast.error("Name and email are required");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        // Only send changed fields
        const patch = {};
        if (form.name !== driver.name) patch.name = form.name.trim();
        if (form.email !== driver.email) patch.email = form.email.trim();
        if (form.phone !== (driver.phone || "")) patch.phone = form.phone.trim() || null;
        if (form.vehicle_make !== (driver.vehicle_make || "")) patch.vehicle_make = form.vehicle_make.trim() || null;
        if (form.vehicle_plate !== (driver.vehicle_plate || "")) patch.vehicle_plate = form.vehicle_plate.trim() || null;
        if (!Object.keys(patch).length) {
          toast("No changes to save");
          setSaving(false);
          return;
        }
        await api.patch(`/admin/drivers/${driver.id}`, patch);
        onSaved(null, false);
      } else {
        const payload = {
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim() || null,
          vehicle_make: form.vehicle_make.trim() || null,
          vehicle_plate: form.vehicle_plate.trim() || null,
        };
        if (form.password.trim()) payload.password = form.password.trim();
        const { data } = await api.post("/admin/drivers", payload);
        onSaved(data?.initial_password || null, true);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="driver-form-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white border-2 border-border w-full max-w-2xl max-h-[90vh] flex flex-col"
        data-testid="driver-form-modal"
      >
        <div className="flex items-center justify-between p-5 pb-3">
          <div>
            <div className="label-mini text-muted-foreground">{isEdit ? "DRIVER PROFILE" : "NEW DRIVER"}</div>
            <h3 className="font-heading text-lg font-black">
              {isEdit ? driver.name : "Add a driver"}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
            data-testid="driver-form-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {isEdit && (
          <div className="flex gap-px px-5 border-b border-border">
            <button
              type="button"
              onClick={() => setTab("details")}
              className={`px-3 py-2 text-xs font-bold transition-colors border-b-2 ${tab === "details" ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-muted-foreground hover:text-foreground"}`}
              data-testid="driver-tab-details"
            >
              Personal &amp; Vehicle
            </button>
            <button
              type="button"
              onClick={() => setTab("compliance")}
              className={`px-3 py-2 text-xs font-bold transition-colors border-b-2 ${tab === "compliance" ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-muted-foreground hover:text-foreground"}`}
              data-testid="driver-tab-compliance"
            >
              Compliance Documents
            </button>
          </div>
        )}

        <div className="overflow-y-auto p-5 pt-3">
          {(!isEdit || tab === "details") && (
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="space-y-2">
                <label className="block">
                  <div className="label-mini text-muted-foreground mb-1">NAME *</div>
                  <input
                    value={form.name}
                    onChange={onField("name")}
                    required
                    className="w-full border border-border px-3 py-2 text-sm focus:outline-none focus:border-[#002FA7]"
                    data-testid="driver-form-name"
                  />
                </label>
                <label className="block">
                  <div className="label-mini text-muted-foreground mb-1">EMAIL *</div>
                  <input
                    type="email"
                    value={form.email}
                    onChange={onField("email")}
                    required
                    className="w-full border border-border px-3 py-2 text-sm focus:outline-none focus:border-[#002FA7]"
                    data-testid="driver-form-email"
                  />
                </label>
                <label className="block">
                  <div className="label-mini text-muted-foreground mb-1">PHONE</div>
                  <input
                    value={form.phone}
                    onChange={onField("phone")}
                    placeholder="+44 7700 900xxx"
                    className="w-full border border-border px-3 py-2 text-sm focus:outline-none focus:border-[#002FA7]"
                    data-testid="driver-form-phone"
                  />
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <label className="block">
                    <div className="label-mini text-muted-foreground mb-1">VEHICLE</div>
                    <input
                      value={form.vehicle_make}
                      onChange={onField("vehicle_make")}
                      placeholder="Skoda Octavia"
                      className="w-full border border-border px-3 py-2 text-sm focus:outline-none focus:border-[#002FA7]"
                      data-testid="driver-form-vehicle-make"
                    />
                  </label>
                  <label className="block">
                    <div className="label-mini text-muted-foreground mb-1">PLATE</div>
                    <input
                      value={form.vehicle_plate}
                      onChange={onField("vehicle_plate")}
                      placeholder="BV21 KDH"
                      className="w-full border border-border px-3 py-2 text-sm uppercase focus:outline-none focus:border-[#002FA7] font-mono-tab"
                      data-testid="driver-form-vehicle-plate"
                    />
                  </label>
                </div>
                {!isEdit && (
                  <label className="block">
                    <div className="label-mini text-muted-foreground mb-1">
                      INITIAL PASSWORD <span className="text-muted-foreground/70 normal-case">(leave blank to auto-generate)</span>
                    </div>
                    <div className="relative">
                      <input
                        type={showPwd ? "text" : "password"}
                        autoComplete="new-password"
                        value={form.password}
                        onChange={onField("password")}
                        placeholder="Auto-generated if blank"
                        className="w-full border border-border px-3 py-2 pr-10 text-sm focus:outline-none focus:border-[#002FA7] font-mono-tab"
                        data-testid="driver-form-password"
                      />
                      <button
                        type="button"
                        tabIndex={-1}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => setShowPwd((v) => !v)}
                        aria-label={showPwd ? "Hide password" : "Show password"}
                        aria-pressed={showPwd}
                        className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-muted-foreground hover:text-foreground"
                        data-testid="driver-form-password-toggle"
                      >
                        {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </label>
                )}
              </div>

              {isEdit && (
                <div className="pt-2 border-t border-border">
                  <button
                    type="button"
                    onClick={sendResetLink}
                    disabled={sendingReset}
                    className="w-full text-xs px-3 py-2 border border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7] hover:text-white font-bold transition-colors disabled:opacity-60 flex items-center justify-center gap-1.5"
                    data-testid="driver-form-send-reset"
                  >
                    {sendingReset ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                    Send password reset link
                  </button>
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Emails {driver.email} a secure, single-use link (expires in 60 min). The driver stays signed in until they choose a new password.
                  </p>

                  {/* Copyable fallback link — always available to the admin,
                      and the primary recovery path when email isn't delivered. */}
                  {resetResult?.reset_url && (
                    <div
                      className={`mt-3 p-2.5 border text-xs ${resetResult.delivered ? "border-border bg-secondary/30" : "border-[#F59E0B] bg-[#FEF3C7]"}`}
                      data-testid="driver-reset-link-box"
                    >
                      {!resetResult.delivered && (
                        <div className="flex items-start gap-1.5 text-[#92400E] font-semibold mb-1.5">
                          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                          <span>Email not delivered ({resetResult.email_status}). Share this secure link directly.</span>
                        </div>
                      )}
                      <div className="label-mini text-muted-foreground mb-1">SECURE RESET LINK · EXPIRES IN {resetResult.expires_in_minutes || 60} MIN</div>
                      <div className="flex items-center gap-1.5">
                        <input
                          readOnly
                          value={resetResult.reset_url}
                          onFocus={(e) => e.target.select()}
                          className="flex-1 min-w-0 font-mono-tab text-[11px] px-2 py-1.5 border border-border bg-white truncate"
                          data-testid="driver-reset-link-input"
                        />
                        <button
                          type="button"
                          onClick={copyResetLink}
                          className="flex-shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-bold border border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7] hover:text-white transition-colors"
                          data-testid="driver-reset-link-copy"
                        >
                          {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                          {copied ? "Copied" : "Copy"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-2 pt-2 border-t border-border">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 text-xs px-3 py-2 border border-border hover:bg-secondary transition-colors"
                  data-testid="driver-form-cancel"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 text-xs px-3 py-2 bg-[#0A0A0A] hover:bg-black text-white font-bold transition-colors disabled:opacity-60 flex items-center justify-center gap-1.5"
                  data-testid="driver-form-submit"
                >
                  {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  {isEdit ? "Save changes" : "Create driver"}
                </button>
              </div>
            </form>
          )}

          {isEdit && tab === "compliance" && (
            <div data-testid="driver-compliance-tab">
              <p className="text-[11px] text-muted-foreground mb-1">
                Upload or replace any document on behalf of {driver.name}, then approve it and set its expiry date.
                Documents you upload are marked "awaiting review" until approved. Personal &amp; vehicle details are on the other tab.
              </p>
              <DocumentReviewer driverId={driver.id} allowUpload onDocChange={() => {}} />
              <div className="flex justify-end pt-3 border-t border-border mt-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="text-xs px-4 py-2 border border-border hover:bg-secondary transition-colors"
                  data-testid="driver-form-close-compliance"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DriverFormModal;
